import React, { useRef, useEffect, useState } from 'react';

const SimpleCameraTest = () => {
  const videoRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('Ready to test');
  const [browserInfo, setBrowserInfo] = useState({});

  useEffect(() => {
    // Detect browser and capabilities
    const detectBrowser = () => {
      const userAgent = navigator.userAgent;
      const isIOS = /iPad|iPhone|iPod/.test(userAgent);
      const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);
      const isSecureContext = window.isSecureContext || window.location.protocol === 'https:' || window.location.hostname === 'localhost';
      
      // Check for getUserMedia support - Safari/iOS requires different detection
      let hasGetUserMedia = false;
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        hasGetUserMedia = true;
      } else if (navigator.webkitGetUserMedia) {
        hasGetUserMedia = true; // Legacy webkit support
      }

      setBrowserInfo({
        userAgent,
        isIOS,
        isSafari,
        isSecureContext,
        hasGetUserMedia,
        protocol: window.location.protocol,
        hostname: window.location.hostname
      });
    };

    detectBrowser();
  }, []);

  const startCamera = async () => {
    setStatus('Requesting camera access...');
    setError(null);

    try {
      // Check if getUserMedia is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('getUserMedia is not supported in this browser');
      }

      // For Safari/iOS, we need to be more specific with constraints
      let constraints = { 
        video: { 
          facingMode: 'environment', // Use environment camera (back camera)
          width: { min: 320, ideal: 640, max: 1280 },
          height: { min: 240, ideal: 480, max: 720 }
        } 
      };

      console.log('Requesting camera with constraints:', constraints);
      let mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      
      // If environment camera fails, try with any camera
      if (!mediaStream) {
        console.log('Environment camera failed, trying any camera...');
        constraints = { 
          video: {
            width: { min: 320, ideal: 640, max: 1280 },
            height: { min: 240, ideal: 480, max: 720 }
          } 
        };
        mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      }

      setStream(mediaStream);
      setStatus('Camera access granted');

      if (videoRef.current && mediaStream) {
        videoRef.current.srcObject = mediaStream;
        
        // For iOS Safari, we need to handle the play promise
        const playPromise = videoRef.current.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              setStatus('Camera is now active');
            })
            .catch(error => {
              console.error('Video play failed:', error);
              setStatus('Camera active but video play failed');
            });
        }
      }

    } catch (err) {
      console.error('Camera error:', err);
      let errorMessage = `Camera error: ${err.name} - ${err.message}`;
      
      // Provide specific help for common iOS/Safari issues
      if (err.name === 'NotAllowedError') {
        errorMessage += '\n\nTip: Make sure to allow camera access when prompted. Check Safari settings: Settings > Safari > Camera.';
      } else if (err.name === 'NotFoundError') {
        errorMessage += '\n\nNo camera found. Make sure your device has a camera.';
      } else if (err.name === 'NotSupportedError') {
        errorMessage += '\n\nCamera not supported. Make sure you\'re using HTTPS or localhost.';
      }
      
      setError(errorMessage);
      setStatus('Camera access failed');
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setStatus('Camera stopped');
  };

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  return (
    <div className="p-6 bg-white rounded-lg shadow">
      <h2 className="text-xl font-bold mb-4">Simple Camera Test</h2>
      
      <div className="space-y-4">
        <div>
          <strong>Status:</strong> <span className="ml-2">{status}</span>
        </div>
        
        {error && (
          <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        <div className="flex space-x-2">
          <button
            onClick={startCamera}
            disabled={!!stream}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-400"
          >
            Start Camera
          </button>
          <button
            onClick={stopCamera}
            disabled={!stream}
            className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 disabled:bg-gray-400"
          >
            Stop Camera
          </button>
        </div>

        <div className="border-2 border-gray-300 rounded-lg overflow-hidden">
          <video
            ref={videoRef}
            width="100%"
            height="300"
            style={{ backgroundColor: '#000', display: 'block' }}
            playsInline
            muted
          />
        </div>

        <div className="text-sm text-gray-600">
          <strong>Browser Info:</strong>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>getUserMedia supported: {browserInfo.hasGetUserMedia ? 'Yes' : 'No'}</li>
            <li>iOS Device: {browserInfo.isIOS ? 'Yes' : 'No'}</li>
            <li>Safari Browser: {browserInfo.isSafari ? 'Yes' : 'No'}</li>
            <li>Protocol: {browserInfo.protocol}</li>
            <li>Hostname: {browserInfo.hostname}</li>
            <li>Is secure context: {browserInfo.isSecureContext ? 'Yes' : 'No'}</li>
            <li>User Agent: {browserInfo.userAgent}</li>
          </ul>
          
          {browserInfo.isIOS && (
            <div className="mt-4 p-3 bg-blue-100 border border-blue-300 rounded">
              <strong>iOS Safari Tips:</strong>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Camera permission must be granted when prompted</li>
                <li>Check Settings {'->'} Safari {'->'} Camera = Allow</li>
                <li>Make sure no other apps are using the camera</li>
                <li>Try refreshing the page after granting permission</li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SimpleCameraTest;
